import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapPin, Plus, X } from 'lucide-react';
import Button from '../ui/Button';
import Drawer from '../ui/Drawer';
import ConfirmDialog from '../ui/ConfirmDialog';
import { apiFetch } from '../../services/apiClient';
import { locationName } from '../../services/locations';
import AddNewButton from '../ui/AddNewButton';
import ItemCard, { CARD_GRID, CardSection } from '../ui/ItemCard';

// =============================================================================
// Clinic groups — which clinics a rule means (Oct 2026).
// =============================================================================
// A rule in the constraints file names a GROUP by its key (IP's cross-cover
// pairs are `ward_pair_5`, …); which clinics are in it is set here, by clinic
// id, so renaming a clinic never breaks a rule. A clinic may be in several
// groups. A group does nothing by itself -- the rules say what it means.
// GET /api/location-groups also lists the keys the active rules use, so a
// group a rule needs but nobody has made yet is offered.
//
// One card per group, edited in a side panel, added from the dashed button at
// the bottom, like every list (Oct 2026).
// =============================================================================

const label = 'text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1';
const box = 'w-full h-[44px] px-4 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]';

const memberName = (schedule, id) => ((schedule?.locations || []).includes(id) ? locationName(schedule, id) : id);

/** Add or edit one group. `groupKey` null = a new one. */
function GroupDrawer({ groupKey, group, schedule, onClose, onSave, saving, error }) {
  const [name, setName] = useState(group?.label || groupKey || '');
  const [members, setMembers] = useState(group?.members || []);
  const addable = (schedule?.locations || []).filter(id => !members.includes(id));
  return (
    <Drawer isOpen onClose={onClose} title={groupKey ? 'Edit Clinic Group' : 'New Clinic Group'}
      footer={(
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onClose} disabled={saving} className="flex-1">Cancel</Button>
          <Button onClick={() => onSave(name.trim(), members)} isLoading={saving} disabled={!name.trim()} className="flex-1">
            {groupKey ? 'Save' : 'Add group'}
          </Button>
        </div>
      )}>
      <div className="space-y-6">
        <div className="flex flex-col gap-1.5">
          <label className={label} htmlFor="group-name">Name</label>
          <input id="group-name" aria-label="Group name" autoFocus value={name}
            onChange={e => setName(e.target.value)} placeholder="e.g. Wards" className={box} />
          {groupKey && (
            <p className="text-[11px] text-slate-400 ml-1">
              Rules refer to it as <code>{groupKey}</code>, which a rename does not change.
            </p>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <span className={label}>Clinics</span>
          <div className="flex flex-wrap gap-1.5">
            {members.map(id => (
              <span key={id} className="flex items-center gap-1 pl-2 pr-1 h-6 rounded-full bg-teal-50 text-teal-800 text-[11px] font-bold">
                {(schedule?.locations || []).includes(id)
                  ? locationName(schedule, id)
                  : <span className="text-rose-500" title="No longer a clinic">{id}</span>}
                <button type="button" aria-label={`Remove ${memberName(schedule, id)}`}
                  onClick={() => setMembers(m => m.filter(x => x !== id))}
                  className="h-4 w-4 flex items-center justify-center rounded-full hover:bg-teal-100"><X className="w-3 h-3" /></button>
              </span>
            ))}
            {members.length === 0 && <span className="text-[11px] text-slate-400 italic">No clinics yet — a rule naming this group matches nothing</span>}
          </div>
          <select aria-label="Add a clinic" className={`${box} cursor-pointer`} value=""
            onChange={e => e.target.value && setMembers(m => [...m, e.target.value])}>
            <option value="">Add a clinic…</option>
            {addable.map(id => <option key={id} value={id}>{locationName(schedule, id)}</option>)}
          </select>
        </div>
        {error && <p className="text-xs font-bold text-rose-600">{error}</p>}
      </div>
    </Drawer>
  );
}

export default function ClinicGroupsCard({ schedule, onSaved }) {
  const [state, setState] = useState(null);       // {groups, missing}
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);   // null | { key: string | null }
  const [removing, setRemoving] = useState(null); // a key
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => apiFetch('/api/location-groups')
    .then(r => r.json())
    .then(d => setState({ groups: d.groups || {}, missing: d.missing || [] }))
    .catch(() => setError('Could not load the clinic groups')), []);
  useEffect(() => { load(); }, [load]);

  /** One request; the parsed body when the server took it, else null. */
  const call = async (path, init) => {
    setError(null); setBusy(true);
    try {
      const res = await apiFetch(path, { headers: { 'Content-Type': 'application/json' }, ...init }).catch(() => null);
      const d = res ? await res.json().catch(() => ({})) : {};
      if (!res || !res.ok) { setError(d.detail || 'That change could not be saved.'); return null; }
      return d;
    } finally { setBusy(false); }
  };
  const done = async () => { await load(); if (onSaved) onSaved(); };

  const put = (key, name, members) => call(`/api/location-groups/${encodeURIComponent(key)}`,
    { method: 'PUT', body: JSON.stringify({ label: name, members }) });

  const save = async (name, members) => {
    let key = editing.key;
    if (!key) {                                   // new: make it (its key comes from the name), then fill it
      const made = await call('/api/location-groups', { method: 'POST', body: JSON.stringify({ label: name }) });
      if (!made) return;
      key = made.key;
      if (!members.length) { setEditing(null); await done(); return; }
    }
    if (await put(key, name, members)) { setEditing(null); await done(); }
  };

  const keys = useMemo(() => Object.keys(state?.groups || {}).sort(), [state]);
  if (!state) return null;

  return (
    <div className="space-y-4">
      <div className="ml-1">
        <h3 className="text-sm font-black text-slate-900 tracking-tight">Clinic groups</h3>
        <p className="text-xs text-slate-500 mt-1 font-medium leading-relaxed max-w-2xl">
          Groups of clinics that the department&apos;s rules refer to — which wards may cover for each
          other, which clinics a limit leaves out. A group does nothing by itself; a rule names its
          key. A clinic may be in several groups, and renaming a clinic never breaks one.
        </p>
      </div>
      {state.missing.length > 0 && (
        <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 font-medium space-y-1.5">
          <div>The department's rules refer to {state.missing.length === 1 ? 'a group that does' : 'groups that do'} not exist yet, so {state.missing.length === 1 ? 'it matches' : 'they match'} no clinic:</div>
          <div className="flex flex-wrap gap-2">
            {state.missing.map(k => (
              <button key={k} type="button" onClick={async () => { if (await put(k, k, [])) await done(); }}
                className="flex items-center gap-1 h-7 px-2 rounded-lg bg-white border border-amber-300 font-bold hover:bg-amber-100">
                <Plus className="w-3 h-3" /> Create <code>{k}</code>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={CARD_GRID}>
        {keys.map(k => {
          const group = state.groups[k];
          const members = group.members || [];
          return (
            <ItemCard key={k} title={group.label || k}
              badges={<code className="text-[10px] text-slate-400" title="The key a rule uses for this group">{k}</code>}
              onEdit={() => { setError(null); setEditing({ key: k }); }}
              onDelete={() => setRemoving(k)}
              footerLeft={`${members.length} clinic${members.length === 1 ? '' : 's'}`}>
              <CardSection icon={MapPin} label="Clinics">
                <div className="flex flex-wrap gap-1.5" data-testid={`clinic-group-${k}`}>
                  {members.map(id => (
                    <span key={id} className={`px-2 py-1 border rounded-md text-[9px] font-bold shadow-sm ${(schedule?.locations || []).includes(id) ? 'bg-slate-50 border-slate-100 text-slate-600' : 'bg-rose-50 border-rose-100 text-rose-500'}`}>
                      {memberName(schedule, id)}
                    </span>
                  ))}
                  {members.length === 0 && <span className="text-[9px] text-slate-300 italic">No clinics yet — matches nothing</span>}
                </div>
              </CardSection>
            </ItemCard>
          );
        })}
      </div>
      {keys.length === 0 && <p className="text-xs text-slate-400 italic ml-1">No clinic groups yet.</p>}

      <AddNewButton label="New group" onClick={() => { setError(null); setEditing({ key: null }); }} />
      {error && !editing && <p className="text-xs font-bold text-rose-600 ml-1">{error}</p>}

      {editing && (
        <GroupDrawer groupKey={editing.key} group={editing.key ? state.groups[editing.key] : null}
          schedule={schedule} saving={busy} error={error}
          onClose={() => { setError(null); setEditing(null); }} onSave={save} />
      )}
      <ConfirmDialog isOpen={Boolean(removing)} onClose={() => setRemoving(null)} tone="danger"
        title={`Remove ${removing ? (state.groups[removing]?.label || removing) : 'this group'}?`}
        body="A rule still naming it will then match no clinic."
        confirmLabel="Remove" isLoading={busy}
        onConfirm={async () => {
          if (await call(`/api/location-groups/${encodeURIComponent(removing)}`, { method: 'DELETE' })) {
            setRemoving(null); await done();
          }
        }} />
    </div>
  );
}
